// @section 1 Campaigns / Leads — engaged-prospect action surface
// Folds in the old Morning Metrics "clickers cohort". A lead = a prospect who
// DID something (clicked demo/diag). Read-only list for now; row actions
// (mark hot / suppress / send 1:1 / push to GHL) come once the write endpoints
// land. Filters: campaign (master) + cohort (demo/diag) + tier + search.

import { useEffect, useMemo, useState } from 'react';
import {
  getCampaignMastersOverview,
  getCampaignLeads,
  CampaignsUnconfigured,
  type CampaignMasterRow,
  type CampaignLead,
} from '../../api';

// @section 2 Main
export default function Leads() {
  const [masters, setMasters] = useState<CampaignMasterRow[] | null>(null);
  const [leads, setLeads] = useState<CampaignLead[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(false);

  // Filters
  const [masterKey, setMasterKey] = useState('');     // '' = all campaigns
  const [cohort, setCohort] = useState('');           // '' | 'demo' | 'diag'
  const [tier, setTier] = useState('');               // '' | 'hot' | 'warm'
  const [search, setSearch] = useState('');

  const handleErr = (e: unknown) => {
    if (e instanceof CampaignsUnconfigured) setUnconfigured(true);
    else setErr(String(e));
  };

  // @subsection 2.1 Load master list for the campaign filter
  useEffect(() => {
    getCampaignMastersOverview().then((d) => setMasters(d.masters)).catch(handleErr);
  }, []);

  // @subsection 2.2 Load leads on filter change (server-side master/cohort/tier;
  // search is sent too but we also filter client-side for instant feedback)
  useEffect(() => {
    setLeads(null); setErr(null);
    getCampaignLeads({
      masterKey: masterKey || undefined,
      cohort: cohort || undefined,
      tier: tier || undefined,
      limit: 500,
    }).then(setLeads).catch(handleErr);
  }, [masterKey, cohort, tier]);

  // @subsection 2.3 Client-side search filter (instant; server already scoped the rest)
  const filtered = useMemo(() => {
    if (!leads) return [];
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      (l.company_name ?? '').toLowerCase().includes(q) ||
      (l.lead_email ?? '').toLowerCase().includes(q) ||
      (l.state ?? '').toLowerCase().includes(q) ||
      (l.utm_campaign ?? '').toLowerCase().includes(q),
    );
  }, [leads, search]);

  return (
    <div className="md:-mx-10 md:-my-8">
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-4 py-5 md:px-10 md:py-7 max-w-[1600px] mx-auto">

          {/* @subsection 2.4 Breadcrumb */}
          <div className="flex items-center justify-between text-xs mb-6">
            <div className="text-muted">
              <span className="text-paper-100">Atlas</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Campaigns</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Leads</span>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-ok">
              <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulse"></span>
              {unconfigured ? 'Pipeline not configured' : 'Live · daily 5 AM AZ refresh'}
            </div>
          </div>

          {/* @subsection 2.5 Title */}
          <div className="mb-6">
            <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">Engaged prospects</div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-paper-100 mb-1">Leads</h1>
            <p className="text-sm text-muted">
              Prospects who clicked a demo or diagnostic link — one row per person, human (GA4) signal.
            </p>
          </div>

          {/* @subsection 2.6 Filter bar */}
          <div className="flex items-center gap-2 flex-wrap mb-5 text-xs">
            <Select value={masterKey} onChange={setMasterKey} label="Campaign">
              <option value="">All campaigns</option>
              {(masters ?? []).map((m) => (
                <option key={m.master_key} value={m.master_key}>{m.master_name}</option>
              ))}
            </Select>
            <Select value={cohort} onChange={setCohort} label="Cohort">
              <option value="">Demo or diag</option>
              <option value="demo">Demo clickers</option>
              <option value="diag">Diag visitors</option>
            </Select>
            <Select value={tier} onChange={setTier} label="Tier">
              <option value="">All tiers</option>
              <option value="hot">Hot</option>
              <option value="warm">Warm</option>
            </Select>
            <input
              placeholder="Search company / email / state…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-ink-900 border border-ink-700 rounded px-3 py-1.5 text-paper-100 w-72"
            />
          </div>

          {/* @subsection 2.7 Table */}
          {!leads && !err && !unconfigured && <Skeleton />}
          {err && <ErrCard msg={err} />}
          {unconfigured && (
            <div className="rounded-xl border-2 border-watch/40 bg-ink-900 p-5 text-sm text-watch">
              Backend returned 503 — awaiting first refresh.
            </div>
          )}
          {leads && leads.length === 0 && !err && (
            <EmptyCard text="No engaged leads for this filter yet." />
          )}
          {leads && leads.length > 0 && (
            <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4 overflow-x-auto">
              <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-2">
                {filtered.length} of {leads.length} leads
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted text-left">
                    <th className="py-1.5">Company</th>
                    <th>Email</th>
                    <th>State</th>
                    <th>Campaign (utm)</th>
                    <th>Arm</th>
                    <th className="text-right">Demo</th>
                    <th className="text-right">Diag</th>
                    <th>Last seen</th>
                    <th>Tier</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.lead_id} className="border-t border-ink-800 hover:bg-ink-800/40">
                      <td className="py-1.5 text-paper-100">{l.company_name ?? '—'}</td>
                      <td className="text-muted">{l.lead_email ?? '—'}</td>
                      <td className="text-muted">{l.state ?? '—'}</td>
                      <td className="text-muted">{l.utm_campaign ?? '—'}</td>
                      <td className="text-muted">{l.arm ?? '—'}</td>
                      <td className="text-right tabular-nums text-accent-400">{l.demo_event_count}</td>
                      <td className="text-right tabular-nums text-ok">{l.diag_event_count}</td>
                      <td className="text-muted tabular-nums">{l.last_seen_date ?? '—'}</td>
                      <td><TierBadge tier={l.tier} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* @subsection 2.8 Actions placeholder — wired once write endpoints land */}
          {leads && leads.length > 0 && (
            <div className="text-[10px] text-muted mt-3">
              Row actions (mark hot · suppress · send 1:1 · push to GHL) land in the next pass —
              this view is read-only for now.
            </div>
          )}

        </div>
      </section>
    </div>
  );
}

// @section 3 Presentational helpers
function Select({ value, onChange, label, children }: {
  value: string; onChange: (v: string) => void; label: string; children: React.ReactNode;
}) {
  return (
    <label className="flex items-center gap-1.5 text-muted">
      <span className="text-[10px] uppercase tracking-wide">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-ink-900 border border-ink-700 rounded px-2 py-1.5 text-paper-100 max-w-[220px] truncate">
        {children}
      </select>
    </label>
  );
}
function TierBadge({ tier }: { tier: string | null }) {
  const t = (tier ?? '').toLowerCase();
  if (t === 'hot') return <span className="px-2 py-0.5 rounded bg-accent-500/20 text-accent-400 text-[10px] uppercase tracking-wide font-semibold">hot</span>;
  if (t === 'warm') return <span className="px-2 py-0.5 rounded bg-ink-800 text-muted text-[10px] uppercase tracking-wide font-semibold">warm</span>;
  return <span className="text-muted text-[10px]">{tier ?? '—'}</span>;
}
function Skeleton() {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-5 animate-pulse">
      <div className="h-4 w-40 bg-ink-800 rounded mb-3"></div>
      <div className="h-3 w-full bg-ink-800/60 rounded mb-2"></div>
      <div className="h-3 w-2/3 bg-ink-800/60 rounded"></div>
    </div>
  );
}
function ErrCard({ msg }: { msg: string }) {
  return <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 text-stall text-xs ring-1 ring-stall/10">{msg}</div>;
}
function EmptyCard({ text }: { text: string }) {
  return <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-6 text-center text-muted text-sm">{text}</div>;
}
